import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ClientService } from '../../../../core/services/client.service';
import { DiaryService } from '../../../../core/services/diary.service';
import { CaseService } from '../../../../core/services/case.service';
import { TransactionForCreation } from '../../../../core/models/transactions/transaction-for-creation.model';
import { TransactionForUpdation } from '../../../../core/models/transactions/transaction-for-updation.model';
import { ClientForListing } from '../../../../core/models/clients/client-for-listing.model';
import { DiaryForListing } from '../../../../core/models/diaries/diary-for-listing.model';
import { CaseForListing } from '../../../../core/models/case/case-for-listing.model';
import { TransactionService } from '../../../../core/services/transaction.service';
import { atLeastOneValidator } from '../../../../core/validators/at-least-one-validator';

@Component({
  selector: 'app-transaction-form',
  standalone: false,
  templateUrl: './transaction-form.component.html',
  styleUrl: './transaction-form.component.css'
})

export class TransactionFormComponent implements OnInit {
  transactionForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  transactionId!: number;
  clientId: number | null = null;
  clients: ClientForListing[] = [];
  diaries: DiaryForListing[] = [];
  cases: CaseForListing[] = [];
  private returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private transactionService: TransactionService,
    private clientService: ClientService,
    private diaryService: DiaryService,
    private caseService: CaseService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Check route params synchronously first to determine edit mode
    const routeParams = this.route.snapshot.params;
    if (routeParams['id']) {
      this.isEditMode = true;
      this.transactionId = +routeParams['id'];
    }

    this.buildForm();

    // Subscribe to route params for loading data
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.transactionId = +params['id'];
        this.loadTransactionData(this.transactionId);
      }
    });
    this.loadClients();
    this.loadDiaries();
    this.loadCases();

    // Get returnUrl from router state, history state, or query params
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['returnUrl']) {
      this.returnUrl = navigation.extras.state['returnUrl'];
    } else if ((window.history.state as any)?.returnUrl) {
      this.returnUrl = (window.history.state as any).returnUrl;
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['returnUrl']) {
          this.returnUrl = params['returnUrl'];
        }
      });
    }

    // Read clientId from query params (regardless of returnUrl)
    // First check synchronously from snapshot
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['clientId']) {
      this.clientId = +queryParams['clientId']; // convert to number
      this.transactionForm.patchValue({ clientId: this.clientId });
    }
    
    // Also subscribe for any changes
    this.route.queryParams.subscribe(params => {
      if (params['clientId']) {
        this.clientId = +params['clientId']; // convert to number
        this.transactionForm.patchValue({ clientId: this.clientId });
      }
    });
  }

  private navigateBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      // If we have a clientId, try to go to client details, otherwise use history or default
      if (this.clientId) {
        this.router.navigate(['/clients/details', this.clientId], {
          queryParams: { activeTab: 'transactions' }
        });
      } else if (window.history.length > 1) {
        this.location.back();
      } else {
        this.router.navigate(['/transactions']);
      }
    }
  }

  private buildForm(): void {
    // Set current date as default when creating (not editing)
    const defaultDate = this.isEditMode ? null : new Date();
    
    this.transactionForm = this.fb.group({
      transactionType: ['', Validators.required],
      amount: [null, Validators.required],
      transactionDate: [defaultDate],
      notes: [''],
      caseId: [null],
      clientId: [null],
      diaryId: [null]
    }, {
      validators: atLeastOneValidator(['clientId', 'caseId', 'diaryId'])
    });
  }

  /**
   * Check if the form has the "at least one required" validation error
   */
  hasAtLeastOneError(): boolean {
    return this.transactionForm.hasError('atLeastOneRequired') && 
           (this.transactionForm.touched || this.isSubmited);
  }

  private loadClients(): void {
    this.clientService.getPaginatedClients({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.clients = res.data,
      error: () => this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 })
    });
  }

  private loadDiaries(): void {
    this.diaryService.getPaginatedDiaries({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.diaries = res.data,
      error: () => this.snackBar.open('Failed to load diaries', 'Close', { duration: 3000 })
    });
  }

  private loadCases(): void {
    this.caseService.getPaginatedCases({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.cases = res.data,
      error: () => this.snackBar.open('Failed to load cases', 'Close', { duration: 3000 })
    });
  }

  private loadTransactionData(id: number): void {
    this.transactionService.getTransactionById(id).subscribe({
      next: (data) => this.transactionForm.patchValue(data),
      error: () => this.snackBar.open('Failed to load transaction details', 'Close', { duration: 3000 })
    });
  }

  submitForm(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.transactionForm.controls).forEach(key => {
      this.transactionForm.get(key)?.markAsTouched();
    });

    if (this.transactionForm.invalid){
      // Keep button enabled if validation fails so user can fix and retry
      this.isSubmited = false;
      return;
    }

    // Only disable button when validation passes and we're submitting
    this.isSubmited = true;

    if (this.isEditMode) {
      const updatedTransaction: TransactionForUpdation = {
        id: this.transactionId,
        ...this.transactionForm.value
      };

      this.transactionService.updateTransaction(updatedTransaction).subscribe({
        next: () => {
          this.snackBar.open('Transaction updated successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to update transaction', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });

    } else {
      const newTransaction: TransactionForCreation = this.transactionForm.value;

      this.transactionService.createTransaction(newTransaction).subscribe({
        next: () => {
          this.snackBar.open('Transaction added successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to add transaction', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }
}

